import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './ui.module.css';
import Button from './Button';
import Icon from './Icon';

interface FilterBarProps {
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  hideSearch?: boolean;
  /**
   * The page's own filter controls. They render inside the panel, laid out on
   * its grid — a page passes plain label/control pairs and positions nothing.
   */
  filters?: React.ReactNode;
  /** How many filters are applied right now. Shown as a badge on the button. */
  activeFilterCount?: number;
  onClearFilters?: () => void;
  /** Controls that must stay visible in the bar itself. Use sparingly. */
  children?: React.ReactNode;
  /** Shown on the left of a bar that has no search — what is filtering the
   *  page right now, e.g. "Core Domestic · 4 schemes". */
  summary?: React.ReactNode;
}

const PANEL_W = 520;
const GAP = 8;

/**
 * Search plus a single Filters control on one rounded bar.
 *
 * Every filter lives behind that one button, however many a page has — that is
 * what keeps the bar the same height on all fifty screens.
 *
 * The panel renders in a **portal on document.body**, positioned from the
 * button's box and clamped to the viewport. An absolutely-positioned panel
 * inside the bar kept getting clipped by ancestors with `overflow: hidden`
 * (cards, table scrollers) or opening off the edge of the screen when the bar
 * had no search box and the button sat at the left. A portal cannot be clipped
 * by an ancestor, and clamping keeps it on screen wherever the button is.
 */
const FilterBar: React.FC<FilterBarProps> = ({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = 'Search…',
  hideSearch = false,
  filters,
  activeFilterCount = 0,
  onClearFilters,
  children,
  summary,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number; maxHeight: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  // MUI's Select/Autocomplete/DatePicker menus render in their own portal
  // straight under document.body, as a sibling of our panel — never a
  // descendant of panelRef. A raw "is the click inside panelRef?" check treats
  // every click on one of those menus as an outside click, closing (and thus
  // unmounting) the panel on mousedown, before the click that picks the
  // option ever fires. So a filter selection never lands — the panel just
  // vanishes. Walk up from the click target and also allow anything sitting
  // inside a MUI popper/modal/menu overlay.
  const isInPortalOverlay = useCallback((node: Node | null): boolean => {
    let el: Element | null = node instanceof Element ? node : node?.parentElement ?? null;
    while (el) {
      // MUI's own slot classes put the keyword right after "Mui" (e.g.
      // "MuiModal-root"), but Autocomplete's popper carries its component
      // name first (e.g. "MuiAutocomplete-popper") — match either shape,
      // case-insensitively, rather than assuming adjacency.
      if (/\bMui[\w-]*(Popover|Modal|Popper|Menu)/i.test(el.className || '')) return true;
      el = el.parentElement;
    }
    return false;
  }, []);

  const place = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    const width = Math.min(PANEL_W, window.innerWidth - GAP * 2);
    // Prefer aligning the panel's right edge to the button's, then clamp.
    const left = Math.min(Math.max(r.right - width, GAP), window.innerWidth - width - GAP);
    const top = r.bottom + 10;
    setPos({ top, left, maxHeight: Math.max(window.innerHeight - top - GAP * 2, 200) });
  }, []);

  // Also runs on mount — the panel stays mounted even while closed (see `panel` below).
  useLayoutEffect(() => {
    place();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (
        !btnRef.current?.contains(t) &&
        !panelRef.current?.contains(t) &&
        !isInPortalOverlay(t)
      ) {
        close();
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    // The button moves with the page, so the panel has to follow it.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close, place, isInPortalOverlay]);

  // Always mounted (closed state is a pure CSS hide), not `open ? ... : null` —
  // some filters (e.g. LastWorkingDateFilter) fetch their default value in a
  // mount-only effect, which needs to run on page load, not on first open.
  const panel = createPortal(
    <div
      ref={panelRef}
      className={`${styles.filterPanel}${open ? '' : ` ${styles.filterPanelHidden}`}`}
      style={{
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        width: Math.min(PANEL_W, window.innerWidth - GAP * 2),
        maxHeight: pos?.maxHeight ?? 0,
      }}
      role="dialog"
      aria-label="Filters"
      aria-hidden={!open}
      inert={!open ? true : undefined}
    >
      <div className={styles.filterPanelHead}>
        <span>Filters</span>
        <span>{activeFilterCount > 0 ? `${activeFilterCount} applied` : 'None applied'}</span>
      </div>
      <div className={styles.filterGrid}>{filters}</div>
      <div className={styles.filterPanelFoot}>
        {onClearFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onClearFilters();
              close();
            }}
            disabled={activeFilterCount === 0}
          >
            Clear all
          </Button>
        )}
        <Button variant="primary" size="sm" onClick={close}>
          Done
        </Button>
      </div>
    </div>,
    document.body,
  );

  return (
    <div className={styles.bar}>
      {!hideSearch && (
        <label className={styles.barSearch}>
          <Icon name="search" size={16} />
          <input
            type="text"
            value={searchValue}
            placeholder={searchPlaceholder}
            onChange={e => onSearchChange?.(e.target.value)}
            aria-label={searchPlaceholder}
          />
          {searchValue && onSearchChange && (
            <button
              type="button"
              className={styles.barClear}
              onClick={() => onSearchChange('')}
              aria-label="Clear search"
              title="Clear search"
            >
              <Icon name="close" size={9} />
            </button>
          )}
        </label>
      )}

      {children}

      {filters && (
        <>
          {!hideSearch && <span className={styles.barDivider} />}
          {hideSearch && (
            <span className={styles.barSummary}>
              <span className={styles.barSummaryLabel}>Showing</span>
              <span className={styles.barSummaryValue}>
                {summary ?? (activeFilterCount > 0
                  ? `${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} applied`
                  : 'All records')}
              </span>
            </span>
          )}
          <button
            ref={btnRef}
            type="button"
            className={`${styles.filterBtn}${activeFilterCount > 0 ? ` ${styles.isSet}` : ''}`}
            aria-expanded={open}
            aria-haspopup="dialog"
            onClick={() => setOpen(v => !v)}
          >
            <Icon name="filter" size={14} />
            Filters
            {activeFilterCount > 0 && <span className={styles.filterCount}>{activeFilterCount}</span>}
            <Icon name="chevronDown" size={12} />
          </button>
          {panel}
        </>
      )}
    </div>
  );
};

export default FilterBar;
